import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchTaskData } from '../../services/taskService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const UserTaskChart = () => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const chartRef = useRef();

  useEffect(() => {
    const loadChartData = async () => {
      try {
        setLoading(true);
        
        // Get task data from API
        const apiResponse = await fetchTaskData(searchParams);
        console.log('API Response:', apiResponse);
        
        // Extract taskStatusList from the response
        const taskList = apiResponse?.taskStatusList || [];
        console.log('Task List to process:', taskList);
        
        const processedData = processTaskData(taskList);
        console.log('Processed chart data:', processedData);
        setChartData(processedData);
        
      } catch (err) {
        console.error('Error loading chart data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadChartData();
  }, [searchParams]);

  const processTaskData = (taskStatusList) => {
    console.log('processTaskData called with:', taskStatusList);
    
    if (!taskStatusList || taskStatusList.length === 0) {
      console.log('No task data provided');
      return null;
    }

    const statusLabel = [];
    const futureList = [];
    const completeList = [];
    const overdueList = [];

    // Filter by status - same as .NET version
    const completeResult = taskStatusList.filter(x => x.status === "Complete");
    const futureResult = taskStatusList.filter(x => x.status === "Future");
    const overdueResult = taskStatusList.filter(x => x.status === "Over Due");

    console.log('Filtered results:', {
      complete: completeResult,
      future: futureResult,
      overdue: overdueResult
    });

    // Create unique createdBy values - same as .NET version
    const uniqueCreatedBy = {};
    taskStatusList.forEach(item => {
      const key = `${item.createdBy}(${item.user_id})`;
      if (!uniqueCreatedBy[key] && item.createdBy !== null) {
        uniqueCreatedBy[key] = key;
      }
    });

    const uniqueObjects = Object.values(uniqueCreatedBy);
    console.log('Unique users:', uniqueObjects);
    uniqueObjects.forEach(obj => statusLabel.push(obj));

    // Here's the key difference: Count records per user per status
    // For each user, count how many Complete/Future/OverDue tasks they have
    uniqueObjects.forEach(userLabel => {
      const userId = userLabel.split('(')[1].split(')')[0];
      
      // Count tasks for this specific user
      const userCompleteCount = completeResult.filter(x => x.user_id === userId).length;
      const userFutureCount = futureResult.filter(x => x.user_id === userId).length;
      const userOverdueCount = overdueResult.filter(x => x.user_id === userId).length;
      
      console.log(`User ${userId} counts:`, {
        complete: userCompleteCount,
        future: userFutureCount,
        overdue: userOverdueCount
      });
      
      completeList.push(userCompleteCount);
      futureList.push(userFutureCount);
      overdueList.push(userOverdueCount);
    });

    console.log('Final chart arrays:', {
      labels: statusLabel,
      complete: completeList,
      future: futureList,
      overdue: overdueList
    });

    return {
      labels: statusLabel,
      datasets: [
        {
          label: 'Complete',
          backgroundColor: '#4eaa4e',
          maxBarThickness: 60,
          data: completeList
        },
        {
          label: 'Over Due',
          backgroundColor: '#eb4c44',
          maxBarThickness: 60,
          data: overdueList
        },
        {
          label: 'Future',
          backgroundColor: '#eab00a',
          maxBarThickness: 60,
          data: futureList
        }
      ]
    };
  };

  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const datasetLabel = chartData.datasets[element.datasetIndex].label;
      const status = chartData.labels[element.index];
      const userId = status.split('(')[1].split(')')[0];
      
      // Navigate to member task details
      const searchParams = new URLSearchParams();
      searchParams.set('userId', userId);
      searchParams.set('status', datasetLabel);
      navigate(`/task/member-details?${searchParams.toString()}`);
    }
  };

  const options = {
    responsive: true,
    onClick: handleChartClick,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Task-User Chart'
      }
    },
    scales: {
      x: {
        maxBarThickness: 60,
        stacked: false
      },
      y: {
        stacked: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  if (loading) {
    return <div className="text-center">Loading chart data...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">Error loading chart: {error}</div>;
  }

  if (!chartData) {
    return (
      <div className="user-task-chart">
        <h2>User Task Chart</h2>
        <div className="alert alert-info">
          <p>No task data available. Please ensure valid user credentials are provided.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-task-chart">
      <h2>User Task Chart</h2>
      <div className="chart-container" style={{ height: '400px' }}>
        <Bar ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

export default UserTaskChart;
